/* Size-guide measurements: slider state, table sync, row reveal.
   One <size-guide-measurements> custom element per guide instance. */

(function () {
  if (customElements.get('size-guide-measurements')) return;

  const DESKTOP_MQ = '(min-width: 1024px)';

  class SizeGuideMeasurements extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-sgm-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-sgm-dot]'));
      this.captionDesc = this.querySelector('[data-sgm-caption-description]');
      this.tableWrap = this.querySelector('.size-guide-measurements__table-wrap');

      if (!this.slides.length) return;

      this.index = 0;
      this.total = this.slides.length;
      this.columnsByIndex = new Map();   // slideIdx -> [cell, cell, ...]
      this.userInitiated = false;        // distinguish manual change for column flash

      this.dots.forEach((d, i) => d.addEventListener('click', () => { this.userInitiated = true; this.go(i); }));

      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.userInitiated = true; this.go(this.index - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this.userInitiated = true; this.go(this.index + 1); }
      });

      this.bindTableSync();
      this.bindRowReveal();
      this.update();
    }

    bindTableSync() {
      if (!window.matchMedia(DESKTOP_MQ).matches) return;
      if (!this.tableWrap) return;

      const labelToIndex = new Map();
      this.slides.forEach((s, i) => {
        const label = (s.dataset.sgmLabel || '').toLowerCase().trim();
        const key = (s.dataset.sgmKey || '').toLowerCase().trim();
        if (label) labelToIndex.set(label, i);
        if (key) labelToIndex.set(key, i);
      });
      if (!labelToIndex.size) return;

      const tables = this.tableWrap.querySelectorAll('table');
      tables.forEach((table) => {
        const headerCells = table.querySelectorAll('thead th, tr:first-child th, tr:first-child td');
        headerCells.forEach((th, colIdx) => {
          const text = (th.textContent || '').toLowerCase().replace(/\s*\(cm\)\s*$/i, '').trim();
          if (!text) return;
          let matchedIdx = -1;
          for (const [label, idx] of labelToIndex) {
            if (text === label || text.startsWith(label) || label.startsWith(text)) {
              matchedIdx = idx;
              break;
            }
          }
          if (matchedIdx < 0) return;

          const colCells = Array.from(table.querySelectorAll('tr')).map((row) => row.children[colIdx]).filter(Boolean);
          const allCells = [th, ...colCells];

          allCells.forEach((cell) => { cell.dataset.measurementRow = String(matchedIdx); });

          if (!this.columnsByIndex.has(matchedIdx)) this.columnsByIndex.set(matchedIdx, []);
          this.columnsByIndex.get(matchedIdx).push(...allCells);

          allCells.forEach((cell) => {
            cell.addEventListener('mouseenter', () => {
              this.go(matchedIdx);
              allCells.forEach((c) => c.classList.add('is-highlighted'));
            });
            cell.addEventListener('mouseleave', () => {
              allCells.forEach((c) => c.classList.remove('is-highlighted'));
            });
          });
        });
      });
    }

    bindRowReveal() {
      if (!this.tableWrap) return;
      if (!('IntersectionObserver' in window)) {
        this.tableWrap.classList.add('is-revealed');
        return;
      }
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            this.tableWrap.classList.add('is-revealed');
            io.disconnect();
          }
        });
      }, { threshold: 0.2 });
      io.observe(this.tableWrap);
    }

    flashColumn(idx) {
      const cells = this.columnsByIndex.get(idx);
      if (!cells || !cells.length) return;
      cells.forEach((c) => c.classList.add('is-flashing'));
      setTimeout(() => cells.forEach((c) => c.classList.remove('is-flashing')), 600);
    }

    go(i) {
      const next = ((i % this.total) + this.total) % this.total;
      const changed = next !== this.index;
      this.index = next;
      this.update();
      if (changed && this.userInitiated && window.matchMedia(DESKTOP_MQ).matches) {
        this.flashColumn(next);
      }
      this.userInitiated = false;
    }

    update() {
      // Replay CSS animation by toggling class off → on.
      this.slides.forEach((s, i) => {
        s.classList.remove('is-active');
        if (i === this.index) {
          // Force reflow so the keyframes restart on each change.
          void s.offsetWidth;
          s.classList.add('is-active');
        }
      });

      this.dots.forEach((d, i) => {
        const active = i === this.index;
        d.classList.toggle('is-active', active);
        d.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      const active = this.slides[this.index];
      if (this.captionDesc) this.captionDesc.textContent = active.dataset.sgmDescription || '';
    }
  }

  customElements.define('size-guide-measurements', SizeGuideMeasurements);
})();
